import { map, Observable, startWith } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectorRef, Component,
  ElementRef, inject, signal, ViewChild, WritableSignal
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { DefaultLogoDirective } from '@app/directives/default-logo.directive';
import { TranslitPipe } from '@app/pipes/translit.pipe';
import { InstagramService } from '@app/services/instagram.service';
import { Tag, TagsService } from '@app/services/tags.service';
import { Person, PersonsService } from '@services/persons.service';
import { UtilsService } from '@services/utils.service';

@Component({
  selector: 'app-search-dialog',
  imports: [AsyncPipe, MatAutocompleteModule, MatFormFieldModule,
    DefaultLogoDirective, MatButtonModule, MatInputModule,
    ReactiveFormsModule, MatIconModule, MatTooltipModule, TranslitPipe],
  templateUrl: './search-dialog.component.html',
  styleUrls: ['./search-dialog.component.scss']
})
export class SearchDialogComponent {
  @ViewChild("searchInput") searchInput!: ElementRef;

  private changeDetectorRef = inject(ChangeDetectorRef);
  private personsService = inject(PersonsService);
  private tagsService = inject(TagsService);
  private instagramService = inject(InstagramService);

  private router = inject(Router);
  private list: any[] = [];
  private storage = 'recent-searches';
  private filtersStorage = 'search-filters';
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  public control = new FormControl('');
  public recentSearches: Person[] = [];
  public filteredItems$!: Observable<{ label: string; items: any[] }[]>;
  public showThreeLetterWarning = true;
  public showNoResultWarning = false;
  public cities: Tag[] = [];
  public tagsList: any[] = [];
  public persons: Person[] = [];
  public surnameList: any[] = [];
  public firstNameList: any[] = [];
  public instagramList: any[] = [];

  public filterInstagram = signal(this.loadFilter('instagram'));
  public filterCities = signal(this.loadFilter('cities'));
  public filterTags = signal(this.loadFilter('tags'));
  public filterSurnames = signal(this.loadFilter('surnames'));
  public filterNames = signal(this.loadFilter('names'));

  private initialSearch = inject<string | null>(MAT_DIALOG_DATA, { optional: true });

  constructor(public dialogRef: MatDialogRef<SearchDialogComponent>) { }

  private loadFilter(key: string): boolean {
    try {
      const saved = localStorage.getItem(this.filtersStorage);
      if (saved) {
        const filters = JSON.parse(saved);
        return filters[key] !== false;
      }
    } catch { }
    return true;
  }

  private saveFilters(): void {
    const filters = {
      instagram: this.filterInstagram(),
      cities: this.filterCities(),
      tags: this.filterTags(),
      surnames: this.filterSurnames(),
      names: this.filterNames(),
    };
    localStorage.setItem(this.filtersStorage, JSON.stringify(filters));
  }

  async ngOnInit() {
    this.setListener();
    await this.getItems();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.searchInput.nativeElement.focus();
      if (this.initialSearch) {
        this.control.setValue(this.initialSearch);
      }
      this.changeDetectorRef.detectChanges();
    }, 300);
  }

  async getItems(): Promise<void> {
    const response = await this.tagsService.getItems();

    const cities = response.filter(t => t.region)
      .map(t => {
        const commonName = t.name;
        const fullTitle = t.licencePlate === 'БГ'
          ? t.region
          : `${t.region} округ`;

        return { ...t, commonName, fullTitle };
      });

    this.tagsList = response.filter(t => !t.region)
      .map(t => ({
        id: t.id,
        commonName: t.name,
        slug: t.slug,
        isTag: true,
        fullTitle: 'Таг',
      }));

    this.cities = cities;
    this.handleLists();

    const persons = await this.personsService.getItems();
    this.persons = persons;
    this.handleSurnames();

    const instagramPosts = await this.instagramService.getItems();
    this.instagramList = instagramPosts.map(post => ({
      id: post.id,
      commonName: post.title,
      imagePath: null as string | null,
      isInstagram: true,
      embeddedCode: post.embeddedCode,
      slug: post.slug ?? '',
      fullTitle: 'Instagram',
    }));
    this.handleLists();
  }

  handleSurnames() {
    const allSurnames = UtilsService.groupAndSortBy(this.persons, 'lastName');
    this.surnameList = [...allSurnames].map(surname => ({
      commonName: surname.title,
      title: surname.title,
      slug: UtilsService.slugify(surname.title),
    }));

    const allFirstNames = UtilsService.groupAndSortBy(this.persons, 'firstName');
    this.firstNameList = allFirstNames
      .filter(name => name.title)
      .map(name => ({
        commonName: name.title,
        isFirstName: true,
        slug: UtilsService.slugify(name.title),
        fullTitle: 'Имена',
      }));

    this.handleLists();
  }

  handleLists() {
    this.list = [
      ...(this.filterInstagram() ? this.instagramList : []),
      ...(this.filterCities() ? this.cities : []),
      ...(this.filterTags() ? this.tagsList : []),
      ...(this.filterNames() ? this.firstNameList : []),
      ...(this.filterSurnames() ? this.surnameList : []),
      ...this.persons,
    ];

    const isPerson = (x: any) => !x.isInstagram && !x.isFirstName && !x.region && !x.title && !x.isTag;

    this.list.sort((a, b) => {
      const aIsPerson = isPerson(a);
      const bIsPerson = isPerson(b);

      if (aIsPerson && !bIsPerson) return -1;
      if (!aIsPerson && bIsPerson) return 1;

      if (aIsPerson && bIsPerson) {
        if (!a.frontPage && b.frontPage) return 1;
        if (a.frontPage && !b.frontPage) return -1;
        if (!a.topTen && b.topTen) return 1;
        if (a.topTen && !b.topTen) return -1;
        if (!a.localHero && b.localHero) return 1;
        if (a.localHero && !b.localHero) return -1;
        if (!a.commonName) return 1;
        if (!b.commonName) return -1;
        return a.commonName.localeCompare(b.commonName);
      }

      if (a.region && !b.region) return -1;
      if (!a.region && b.region) return 1;
      if (a.isTag && !b.isTag) return -1;
      if (!a.isTag && b.isTag) return 1;
      if (a.isFirstName && !b.isFirstName) return -1;
      if (!a.isFirstName && b.isFirstName) return 1;
      if (a.title && !b.title) return -1;
      if (!a.title && b.title) return 1;
      if (a.isInstagram && !b.isInstagram) return -1;
      if (!a.isInstagram && b.isInstagram) return 1;

      return (a.commonName ?? '').localeCompare(b.commonName ?? '');
    });

    this.getRecentSearches();
  }

  setListener() {
    this.filteredItems$ = this.control.valueChanges.pipe(
      startWith(''),
      map(item => this.filterList(item || '')),
    );
  }

  filterList(value: string): { label: string; items: any[] }[] {
    const filterValue = value.toLowerCase();

    const textSlugify: string = UtilsService.slugify(filterValue);

    if (textSlugify.length < 3) {
      this.showThreeLetterWarning = true;
      this.showNoResultWarning = false;
      return [];
    }

    const result = this.list.filter(e => {
      const commonNameSlugify = UtilsService.slugify(e.commonName);
      const nameMatches = commonNameSlugify.toLowerCase().includes(textSlugify);
      if (nameMatches) {
        return true;
      }

      const fullNameSlugify = UtilsService.slugify(e.fullName);
      const fullNameMatches = fullNameSlugify.toLowerCase().includes(textSlugify);
      if (fullNameMatches) {
        return true;
      }

      const nickNameSlugify = UtilsService.slugify(e.nickName);
      const nickNameMatches = nickNameSlugify.toLowerCase().includes(textSlugify);
      if (nickNameMatches) {
        return true;
      }

      return false;
    });

    if (!result?.length) {
      this.showThreeLetterWarning = false;
      this.showNoResultWarning = true;
    } else {
      this.showThreeLetterWarning = false;
      this.showNoResultWarning = false;
    }

    const isPerson = (x: any) => !x.isInstagram && !x.isFirstName && !x.region && !x.title && !x.isTag;

    return [
      { label: 'Личности', items: result.filter(isPerson) },
      { label: 'Градови и општинe', items: result.filter((x: any) => x.region) },
      { label: 'Тагови', items: result.filter((x: any) => x.isTag) },
      { label: 'Имена и презимена', items: result.filter((x: any) => x.isFirstName || x.title) },
      { label: 'Инстаграм објаве', items: result.filter((x: any) => x.isInstagram) },
    ].filter(g => g.items.length > 0);
  }

  getRecentSearches() {
    const storageRecentSearches = localStorage.getItem(this.storage);
    if (storageRecentSearches) {
      this.recentSearches = JSON.parse(storageRecentSearches);
      this.cdr.detectChanges();
    }
  }

  addToRecentSearches(recent: Person) {
    this.recentSearches.unshift(recent);
    let uniqueList = UtilsService.removeDuplicatesByKey(this.recentSearches, 'id');
    uniqueList = uniqueList.slice(0, 5);
    const data = JSON.stringify(uniqueList);
    localStorage.setItem(this.storage, data);
  }

  toggleFilter(filter: WritableSignal<boolean>): void {
    filter.update(v => !v);
    this.saveFilters();
    this.handleLists();
    this.control.setValue(this.control.value ?? '', { emitEvent: true });
  }

  toggleNamesAndSurnames(): void {
    const newValue = !this.filterNames();
    this.filterNames.set(newValue);
    this.filterSurnames.set(newValue);
    this.saveFilters();
    this.handleLists();
    this.control.setValue(this.control.value ?? '', { emitEvent: true });
  }

  onItem(item: any) {
    if (item.isInstagram) {
      this.onInstagramPost(item);
    } else if (item.isFirstName) {
      this.onFirstName(item);
    } else if (item.title) {
      this.onSurname(item);
    } else if (item.region || item.isTag) {
      this.onCity(item);
    } else {
      this.onBiography(item);
    }
  }

  onInstagramPost(item: any) {
    if (item.embeddedCode) {
      const dropPrefix = item.embeddedCode.split('https')[1];
      if (dropPrefix) {
        const url = 'https' + dropPrefix.split('?utm_source')[0];
        window.open(url, '_blank');
      }
    }
    this.dialogRef.close();
  }

  onSurname(item: Person) {
    this.navigate(`prezime/${item.slug}`);
  }

  onFirstName(item: any) {
    this.navigate(`ime/${item.slug}`);
  }

  onCity(item: Person) {
    this.navigate(`tag/${item.slug}/podaci`, item);
  }

  onBiography(item: Person) {
    this.navigate(`biografija/${item.slug}`, item);
  }

  navigate(path: string, item?: any) {
    this.router.navigateByUrl(path)
      .then(() => {
        this.changeDetectorRef.detectChanges();
        this.dialogRef.close();
        if (item && !item.title) {
          this.addToRecentSearches(item);
        }
      });
  }

  onClose() {
    this.dialogRef.close(true);
  }
}
